import {type ReactNode, useCallback, useState} from 'react'

import {AgentAuthorizationDialog} from '../AgentAuthorizationDialog'
import {mockAgentAuthState, requestAuthorizationUrl} from '../mock-client'
import type {AgentAuthorizationAgent} from '../types'

export type AgentAuthorizationGuard = {
  /** Returns true if the agent hasn't been authorized (gated by `copilot_swe_agent_authorization_status_ui`). */
  requiresAuth: (slug: string, displayName: string, avatarUrl?: string) => boolean
  /** Opens the authorization dialog for the given agent. */
  openDialog: (agent: AgentAuthorizationAgent) => void
  /** Dialog JSX + its open-state machinery; render inline in the host. */
  dialog: ReactNode
}

function requiresAuth(slug: string, displayName: string, avatarUrl?: string): boolean {
  return mockAgentAuthState(slug, displayName, avatarUrl).requiresAuthentication
}

/**
 * Picker-surface guard: call `requiresAuth(...)` before committing a selection, then
 * `openDialog(agent)` and render `dialog`. Authorize POSTs the real endpoint when `nwo`
 * + `integrationId` are available, otherwise falls back to a mock URL.
 */
export function useAgentAuthorizationGuard(options: {nwo?: string} = {}): AgentAuthorizationGuard {
  const {nwo} = options
  const [activeAgent, setActiveAgent] = useState<AgentAuthorizationAgent | null>(null)
  const [isAuthorizing, setIsAuthorizing] = useState(false)
  const [authorizationError, setAuthorizationError] = useState<string | undefined>(undefined)

  const openDialog = useCallback((agent: AgentAuthorizationAgent) => {
    setActiveAgent(agent)
    setIsAuthorizing(false)
    setAuthorizationError(undefined)
  }, [])

  const closeDialog = useCallback(() => {
    setActiveAgent(null)
    setIsAuthorizing(false)
    setAuthorizationError(undefined)
  }, [])

  const handleAuthorize = useCallback(async () => {
    if (!activeAgent) return
    setIsAuthorizing(true)
    setAuthorizationError(undefined)
    try {
      const url = await requestAuthorizationUrl(
        {slug: activeAgent.slug, integrationId: activeAgent.integrationId},
        {nwo},
      )
      // Single-use URL — redirect via assign() so back button works.
      window.location.assign(url)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[agent-authorization] failed to fetch authorization URL', error)
      setIsAuthorizing(false)
      setAuthorizationError(`Couldn't start authorization for ${activeAgent.displayName}. Please try again.`)
    }
  }, [activeAgent, nwo])

  const dialog = activeAgent ? (
    <AgentAuthorizationDialog
      agent={activeAgent}
      onClose={closeDialog}
      onAuthorize={handleAuthorize}
      isAuthorizing={isAuthorizing}
      authorizationError={authorizationError}
    />
  ) : null

  return {requiresAuth, openDialog, dialog}
}
